<template>
  <ArticleLayout :article="article">
    <div
      class="article-single__content-editor article-single__content-editor--no-border content-editor"
      :class="article.settings.cssClass"
      :style="{ 'font-family': article.settings.fontFamily }"
      @click="onDocContentClick"
    >
      <img class="article-single__cover" :src="article.cover_image" alt="" />
      <Annotator :content="article.page">
        <div class="sf-chapter" oncopy="return false" v-html="getHtml()"></div>
        <EOCButton
          v-if="article.isChapterEOC"
          text="Subscribe to Read Full Article"
          ios-text="Full Subscription Required to Read More"
        />
      </Annotator>
    </div>
    <AudioPlayer
      v-if="article.audio && article.controls.isPlayerRendered"
      :doc="article"
    />
    <AudioPlayerDisabled
      v-if="
        !article.audio && article.has_audio && article.controls.isPlayerRendered
      "
      :class="article.controls.playerClass"
    />
  </ArticleLayout>
</template>

<script setup lang="ts">
import { onBeforeMount } from 'vue'

import ArticleLayout from '@/app/components/article/Layout.vue'

import AudioPlayer from '@/app/components/doc/player/Player.vue'
import AudioPlayerDisabled from '@/app/components/doc/player/PlayerDisabled.vue'
import Annotator from '@/app/components/doc/reading/Annotator.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'

import { ArticleView } from '@/models/doc.article'
import { emitter } from '@/services/mitt'
import { formatDate } from '@/helpers/time'
import { parseTextForTranslation } from '@/services/weglot'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  article: ArticleView
}>()

onBeforeMount(async () => {
  Mixpanel.setFirstDoc('Viewed', props.article.title, props.article.doc_type)
})

function publishDate(): string {
  if (props.article.original_publish_date) {
    return formatDate(props.article.original_publish_date)
  }
  return ''
}

/**
 * We use the `getHtml` function instead of writing this directly since
 * 1) we want to use the v-html directive to render article.page.text
 * 2) the title, author, and blurb need to be annotatable, which means
 *    they need to be children of the `.sf-chapter` div.
 * See issue #4109.
 */
function getHtml(): string {
  // If the blurb exists, we add H3 after it, which renders the horizontal line.
  const blurbHtml =
    `<p>${props.article.blurb}</p>` + (props.article.blurb ? '<h3></h3>' : '')

  return `<div>
        <h2 class="article-single__title">${props.article.title}</h2>
        <p>
          <span class="article-single__author text-gray">
            By ${props.article.author}
            <span class="line"></span>
            <a
              href="${props.article.original_url}"
              target="_blank"
              class="article-single__publisher-link"
            >
              Published ${publishDate()} in
              <em>${props.article.publisher.name}</em></a
            >
            ${getSubscribeLink()}
          </span>
        </p>
        ${blurbHtml}
     </div>
     ${parseTextForTranslation(props.article.page.text)}`
}

function getSubscribeLink(): string {
  if (!props.article.publisher.subscribe_url) {
    return ''
  }
  return `
      <span class="line"></span>
      <a
        href="${props.article.publisher.subscribe_url}"
        target="_blank"
      >Subscribe</a>`
}

function onDocContentClick(event: any): void {
  emitter.emit('docContentClicked', event)
}
</script>
